// Other Dependencies:
// - languageSelectView.scss

import * as preact from "preact";

import * as languageMap from "shared/languageMap";
import stores from "stores/stores";
import { type UserAttrs } from "stores/UserStore";

type MountElement = string | JQuery<Element> | Element;
type LanguageSelectField = "currentLearningLanguage" | "firstLanguage";

export interface LanguageSelectProps {
  field: LanguageSelectField;
  switchToMonolingualIfSame?: boolean;
  learningLanguages?: boolean;
  disabled?: boolean;
  confirmationPrompt?: string;
  flipIfSame?: boolean;
  english?: boolean;
  hint?: string;
  onChange?: (languageCode: string) => void;
}

const getElement = (el: MountElement): Element => {
  if (typeof el === "string") {
    const element = document.querySelector(el);
    if (!element) throw new Error(`Mount element not found: ${el}`);
    return element;
  }
  if (el instanceof Element) {
    return el;
  }
  const element = el[0];
  if (!element) throw new Error("Mount element not found");
  return element;
};

/**
 * Sets the user's learning or first language. Choosing the other field's
 * language either swaps the two (`flipIfSame`) or toggles translations off
 * and on (`switchToMonolingualIfSame`). Returns whether a language changed.
 */
export const selectLanguage = (
  field: LanguageSelectField,
  newCode: string,
  options: { flipIfSame?: boolean; switchToMonolingualIfSame?: boolean }
): boolean => {
  const user = stores.user;
  const complementaryField: LanguageSelectField =
    field === "firstLanguage" ? "currentLearningLanguage" : "firstLanguage";
  const complementaryValue =
    complementaryField === "currentLearningLanguage"
      ? user.currentLearningLanguage.value
      : user.firstLanguage.value;
  if (newCode === complementaryValue) {
    if (options.switchToMonolingualIfSame) {
      user.save({
        readerTranslations:
          user.readerTranslations.value === "on" ? "off" : "on",
      });
      return false;
    }
    if (options.flipIfSame) {
      user.save({
        [field]: complementaryValue,
        [complementaryField]: user.state.value[field],
      } as UserAttrs);
      return true;
    }
  }

  if (field === "firstLanguage") {
    user.selectFirstLanguage(newCode);
  } else {
    user.selectLearningLanguage(newCode);
  }
  return true;
};

export class LanguageSelect extends preact.Component<LanguageSelectProps> {
  getSelectedValue() {
    const user = stores.user;
    const field = this.props.field;
    return field === "currentLearningLanguage"
      ? user.currentLearningLanguage.value
      : user.firstLanguage.value;
  }

  handleChange = (event: Event) => {
    if (
      this.props.confirmationPrompt &&
      !confirm(this.props.confirmationPrompt)
    ) {
      (event.currentTarget as HTMLSelectElement).value = String(
        this.getSelectedValue() ?? ""
      );
      return;
    }

    const newCode = (event.currentTarget as HTMLSelectElement).value;
    if (selectLanguage(this.props.field, newCode, this.props)) {
      this.props.onChange?.(newCode);
    }
  };

  reEnableTranslation = (event: MouseEvent) => {
    const user = stores.user;
    if (
      this.props.switchToMonolingualIfSame &&
      user.readerTranslations.value === "off"
    ) {
      user.save({ readerTranslations: "on" });
      event.preventDefault();
      event.stopPropagation();
    }
  };

  render() {
    const user = stores.user;
    const list = this.props.learningLanguages
      ? languageMap.learningList
      : languageMap.list;
    const readerTranslationsDisabled =
      this.props.field === "firstLanguage" &&
      user.readerTranslations.value === "off";
    const disabled = readerTranslationsDisabled || this.props.disabled;

    return (
      <>
        <select
          disabled={disabled}
          onChange={this.handleChange}
          title={this.props.hint}
          value={String(this.getSelectedValue() ?? "")}
        >
          {list.map((language) => (
            <option
              data-languageCode={language.code}
              key={language.code}
              title={this.props.english ? language.name : language.english}
              value={language.code}
            >
              {this.props.english ? language.english : language.name}
            </option>
          ))}
        </select>
        {readerTranslationsDisabled ? (
          <div class="reEnableTranslation" onClick={this.reEnableTranslation} />
        ) : null}
      </>
    );
  }
}

export interface LanguageSelectViewOptions extends LanguageSelectProps {
  el: MountElement;
}

export default class LanguageSelectView {
  private readonly element: Element;
  private readonly options: LanguageSelectViewOptions;

  constructor(options: LanguageSelectViewOptions) {
    this.element = getElement(options.el);
    this.options = options;
    this.element.classList.add("languageSelectView");
    this.element.textContent = "";
    this.render();
  }

  render() {
    const { el: _el, ...props } = this.options;
    preact.render(<LanguageSelect {...props} />, this.element);
    return this;
  }

  remove() {
    preact.render(null, this.element);
    return this;
  }
}
